import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { CollectionModel } from '../../classes/collection.model';
import * as CollectionActions from '../actions/collection.actions';

export const collectionsFeatureKey = 'collections';

export interface CollectionsState extends EntityState<CollectionModel> {
  loaded: boolean;
}

export const adapter: EntityAdapter<CollectionModel> = createEntityAdapter<CollectionModel>();

export const initialState: CollectionsState = adapter.getInitialState({
  loaded: false,
});

export const reducer = createReducer(
  initialState,
  on(CollectionActions.addedCollectionSuccess, (state, action) => adapter.addOne(action.collection, state)),
  on(CollectionActions.upsertCollection, (state, action) => adapter.upsertOne(action.collection, state)),
  on(CollectionActions.addCollections, (state, action) => adapter.addMany(action.collections, state)),
  on(CollectionActions.upsertCollections, (state, action) => adapter.upsertMany(action.collections, state)),
  on(CollectionActions.updatedCollectionSuccess, (state, action) => adapter.updateOne(action.collection, state)),
  on(CollectionActions.deletedCollection, (state, action) => adapter.removeOne(action.id, state)),
  on(CollectionActions.loadedCollections, (state, action) =>
    adapter.setAll(action.collections, { ...state, loaded: true }),
  ),
  on(CollectionActions.clearCollections, state => adapter.removeAll(state)),
);

export const getCollectionsState = createFeatureSelector<CollectionsState>('collections');

export const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors(getCollectionsState);


export const collectionsHasLoaded = createSelector(getCollectionsState, (state: CollectionsState) => state.loaded);

export const getItemById = id => createSelector(getCollectionsState, state => state.entities[id]);

export const getRootCollections = () =>
  createSelector(selectAll, objects => objects.filter(object => !object.parentCollectionId));

export const getCollectionsByIds = (ids: string[]) =>
  createSelector(selectAll, objects =>
    objects.filter(object => ids.includes(object.id)),
  );

export const getRootCollectionsWithChildCount = (selector = selectAll) =>
  createSelector(selector, objects =>
    objects
      .filter(object => !object.parentCollectionId)
      .map(collection => {
        return {
          ...collection,
          childCount: objects.filter(object => object.parentCollectionId === collection.id).length,
        };
      }),
  );

export const getRootCollectionsWithChildren = (selector = selectAll) =>
  createSelector(selector, objects =>
    objects
      .filter(object => !object.parentCollectionId)
      .map(collection => {
        return {
          ...collection,
          children: objects.filter(object => object.parentCollectionId === collection.id),
        };
      }),
  );

export const getCollectionsByParentId = (id: string) => // Added type for id
  createSelector(
    selectAll,
    (objects: CollectionModel[]) => { // Added type for objects
      console.log(`[SELECTOR getCollectionsByParentId for '${id}'] Projector RUNNING. Input objects count: ${objects.length}`);
      // If objects.length > 0, inspect the first few to check parentCollectionId values
      // if (objects.length > 0) {
      //   console.log(`First object's parentCollectionId:`, objects[0].parentCollectionId);
      // }
      const filtered = objects.filter(object => object.parentCollectionId === id);
      console.log(`[SELECTOR getCollectionsByParentId for '${id}'] Projector OUTPUT. Filtered count: ${filtered.length}`);
      return filtered;
    }
  );

export const getCollectionsByParentIdAndCount = id =>
  createSelector(selectAll, objects =>
    objects
      .filter(object => object.parentCollectionId === id)
      .map(collection => {
        return {
          ...collection,
          childCount: objects.filter(object => object.parentCollectionId === collection.id).length,
        };
      }),
  );

export const getCollectionCountByParentId = id =>
  createSelector(selectAll, objects =>
    objects.filter(object => object.parentCollectionId === id).length,
  );

export const getRootCollectionByChildId = id => {
  return createSelector(selectAll, objects => {
    let rootCollection = objects.find(object => object.id === id);

    if (!rootCollection) {
      return null; // If no collection found with the given id, return null
    }

    while (rootCollection.parentCollectionId){
      let tmpRootCollection = objects.find(object => object.id === rootCollection.parentCollectionId);
      if (!tmpRootCollection) {
        break; // If no parent found, exit the loop
      }
      rootCollection = tmpRootCollection;
    }

    return rootCollection;
  });
};

export const getCollectionsByParentIdAndChildren = id =>
  createSelector(selectAll, objects =>
    objects
      .filter(object => object.parentCollectionId === id)
      .map(collection => {
        return {
          ...collection,
          children: objects.filter(object => object.parentCollectionId === collection.id),
        };
      }),
  );

